import React, { useMemo, useState } from 'react'

import {
  CalendarAddIcon,
  GiftCardIcon,
  MapIcon,
  OnlineOrderingIcon,
  PhoneIcon
} from '@toasttab/buffet-pui-icons'

import { DiningOptionBehavior, Location } from 'src/apollo/onlineOrdering'
import {
  emptySchedule,
  formatTimeString,
  getDay,
  getToday,
  getCurrentScheduleInterval,
  FullSchedule
} from 'src/lib/js/schedule'
import { t } from 'src/translations'

import { getGoogleMapsDirectionsUrl } from 'public/components/online_ordering/addressUtils'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import ClosingTimeAlert from 'public/components/default_template/online_ordering/dining_behavior_toggle/ClosingTimeAlert'
import DiningBehaviorSummary from 'public/components/default_template/online_ordering/dining_behavior_toggle/DiningBehaviorSummary'
import FulfillmentTimeSummary from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentTimeSummary'

import { resources } from 'config'

import { RestaurantProfileScheduleType } from '../profileUtils'
import HeaderImage from '../header_image/HeaderImage'

type FulfillmentOption = 'pickup' | 'delivery'

// Attribution for the Reserve link. The reservation page is a separate app that
// picks utm params up via Amplitude autocapture, so this is how a booking there
// can be traced back to this order page.
const RESERVE_UTM_SOURCE = 'order_page'

const buildReserveUrl = (host: string, shortUrl: string): string => {
  const url = new URL(`https://${host}/r/${shortUrl}`)
  url.searchParams.append('utm_source', RESERVE_UTM_SOURCE)
  return url.toString()
}

type Props = {
  id: string
  rxName: string
  location: Location
  bannerImageUrl?: string
  cuisineType?: string
  schedule?: RestaurantProfileScheduleType
  timeZoneId?: string
  phone?: string | null
  giftCardHref?: string | null
  websiteHref?: string | null
  restaurantShortUrl?: string
  hasReservations?: boolean
}

const RestaurantDetailsHeader = ({
  id,
  rxName,
  location,
  bannerImageUrl,
  cuisineType,
  schedule,
  timeZoneId,
  phone,
  giftCardHref,
  websiteHref,
  restaurantShortUrl,
  hasReservations = false
}: Props) => {
  const fullSchedule: FullSchedule | undefined = useMemo(() => {
    if (!schedule?.upcomingSchedules || !timeZoneId) {
      return undefined
    }

    const hours = emptySchedule()
    // Filter to TakeOut schedule first, fallback to first available schedule
    // to avoid different behaviors (TakeOut/Delivery) overwriting each other
    const scheduleToUse =
      schedule.upcomingSchedules.find(
        (s) => s.behavior === DiningOptionBehavior.TakeOut
      ) || schedule.upcomingSchedules[0]

    if (scheduleToUse) {
      scheduleToUse.dailySchedules.forEach((dailySchedule) => {
        const scheduleDate = new Date(dailySchedule.date)
        const day = getDay(scheduleDate, true)
        hours[day].intervals = dailySchedule.servicePeriods
      })
    }

    return hours
  }, [schedule, timeZoneId])

  const { isOpen, hoursText } = useMemo(() => {
    if (!fullSchedule || !timeZoneId) {
      return { isOpen: false, hoursText: '' }
    }

    // Use getCurrentScheduleInterval to check if currently open
    const currentInterval = getCurrentScheduleInterval(fullSchedule, timeZoneId)
    const currentlyOpen = !!currentInterval

    // Get hours text from today's intervals
    const today = getToday(timeZoneId)
    const todayIntervals = fullSchedule[today]?.intervals || []

    let hoursText = ''
    if (todayIntervals.length > 0) {
      const firstInterval = todayIntervals[0]
      const lastInterval = todayIntervals[todayIntervals.length - 1]
      if (firstInterval && lastInterval) {
        const startTime = formatTimeString(firstInterval.startTime)
        const endTime = formatTimeString(lastInterval.endTime)
        hoursText = `${startTime} - ${endTime}`
      }
    }

    return { isOpen: currentlyOpen, hoursText }
  }, [fullSchedule, timeZoneId])

  const directionsHref = useMemo(
    () => getGoogleMapsDirectionsUrl(location),
    [location]
  )

  const reserveHref = useMemo(
    () =>
      restaurantShortUrl
        ? buildReserveUrl(resources.toastAppHost, restaurantShortUrl)
        : undefined,
    [restaurantShortUrl]
  )

  return (
    <div className='restaurant-details-header'>
      <HeaderImage
        id={id}
        rxName={rxName}
        location={location}
        bannerImageUrl={bannerImageUrl}
      />
      <div className='header-row'>
        <h1 className='restaurant-name'>{rxName}</h1>
        <FulfillmentToggle />
      </div>
      <div className='content-grid'>
        <div className='left-section'>
          {cuisineType && <div className='cuisine-type'>{cuisineType}</div>}
          {schedule && timeZoneId && (
            <div className='hours-status'>
              <span className={`status ${isOpen ? 'open' : 'closed'}`}>
                {isOpen ? t('open-status.open-now') : t('open-status.closed')}
              </span>
              {hoursText && <span className='hours'>{hoursText}</span>}
            </div>
          )}
          <div className='action-buttons'>
            {phone && (
              <a href={`tel:${phone}`} className='action-button'>
                <PhoneIcon aria-label={t('overview.phone-icon')} />
                <span>{t('overview.cta-action.call')}</span>
              </a>
            )}
            <a
              href={directionsHref}
              className='action-button'
              target='_blank'
              rel='noreferrer noopener'
            >
              <MapIcon aria-label={t('overview.waypoint-sign-icon')} />
              <span>{t('overview.cta-action.directions')}</span>
            </a>
            {websiteHref && (
              <a
                href={`https://${websiteHref}`}
                className='action-button'
                target='_blank'
                rel='noreferrer noopener'
              >
                <OnlineOrderingIcon aria-label={t('overview.website-icon')} />
                <span>{t('overview.cta-action.website')}</span>
              </a>
            )}
            {giftCardHref && (
              <a
                href={giftCardHref}
                className='action-button'
                target='_blank'
                rel='noreferrer noopener'
              >
                <GiftCardIcon aria-label={t('overview.gift-icon')} />
                <span>{t('overview.cta-action.gift-cards')}</span>
              </a>
            )}
            {hasReservations && reserveHref && (
              <a
                href={reserveHref}
                className='action-button'
                target='_blank'
                rel='noreferrer noopener'
              >
                <CalendarAddIcon aria-label={t('overview.reserve-icon')} />
                <span>{t('fulfillment-general.reserve')}</span>
              </a>
            )}
          </div>
        </div>
        <div className='right-section'>
          <div className='fulfillment-summaries'>
            <DiningBehaviorSummary />
            <FulfillmentTimeSummary />
            <ClosingTimeAlert />
          </div>
        </div>
      </div>
    </div>
  )
}

const FulfillmentToggle = () => {
  const {
    selectedDiningOptionBehavior,
    setSelectedDiningOptionBehavior,
    updateCartFulfillment,
    fulfillmentData,
    canOrderTakeout,
    canOrderDelivery
  } = useFulfillment()
  const { lastKnownValidDeliveryAddress } = useDelivery()
  const [isUpdating, setIsUpdating] = useState(false)

  const hasOrderingOptions = canOrderTakeout || canOrderDelivery

  const activeOption: FulfillmentOption = useMemo(() => {
    // If no ordering options available, default to pickup
    if (!hasOrderingOptions) {
      return 'pickup'
    }
    // If ordering options exist, check what's selected and if it's available
    if (
      selectedDiningOptionBehavior === DiningOptionBehavior.Delivery &&
      canOrderDelivery
    ) {
      return 'delivery'
    } else if (
      selectedDiningOptionBehavior === DiningOptionBehavior.TakeOut &&
      canOrderTakeout
    ) {
      return 'pickup'
    }
    // Default fallback
    if (canOrderTakeout) return 'pickup'
    if (canOrderDelivery) return 'delivery'
    return 'pickup'
  }, [
    selectedDiningOptionBehavior,
    hasOrderingOptions,
    canOrderTakeout,
    canOrderDelivery
  ])

  const handleFulfillmentChange = async (option: FulfillmentOption) => {
    if (isUpdating) return

    const behavior =
      option === 'delivery'
        ? DiningOptionBehavior.Delivery
        : DiningOptionBehavior.TakeOut

    if (behavior === selectedDiningOptionBehavior) return

    setIsUpdating(true)
    const previousBehavior = selectedDiningOptionBehavior
    try {
      setSelectedDiningOptionBehavior(behavior)

      if (fulfillmentData?.cartFulfillmentData) {
        await updateCartFulfillment({
          fulfillmentType: fulfillmentData.cartFulfillmentData.fulfillmentType,
          fulfillmentDateTime:
            fulfillmentData.cartFulfillmentData.fulfillmentDateTime,
          diningOptionBehavior: behavior,
          deliveryInfo:
            behavior === DiningOptionBehavior.Delivery
              ? lastKnownValidDeliveryAddress ??
                fulfillmentData.cartFulfillmentData.deliveryInfo ??
                null
              : null
        })
      }
    } catch (error) {
      // Rollback UI state on failure
      setSelectedDiningOptionBehavior(previousBehavior)
    } finally {
      setIsUpdating(false)
    }
  }

  const getSliderPosition = () => {
    if (activeOption === 'pickup') {
      return 0
    } else if (activeOption === 'delivery') {
      return canOrderTakeout ? 1 : 0
    }
    return 0
  }

  // Calculate total buttons based on what's available
  let totalButtons = 0
  if (hasOrderingOptions) {
    if (canOrderTakeout) totalButtons++
    if (canOrderDelivery) totalButtons++
  } else {
    // If no ordering, show just "Order" button
    totalButtons = 1
  }

  const sliderWidth = `calc(${100 / totalButtons}% - (${
    (totalButtons - 1) * 4
  }px / ${totalButtons}))`

  return (
    <div className='fulfillment-toggle'>
      <div
        className='toggle-slider'
        style={{
          transform: `translateX(calc(${getSliderPosition()} * (100% + 4px)))`,
          width: sliderWidth
        }}
      />
      {hasOrderingOptions ? (
        <>
          {canOrderTakeout && (
            <button
              className={`toggle-option ${
                activeOption === 'pickup' ? 'active' : ''
              }`}
              onClick={() => handleFulfillmentChange('pickup')}
              disabled={isUpdating}
              type='button'
              aria-pressed={activeOption === 'pickup'}
            >
              {t('fulfillment-general.pickup')}
            </button>
          )}
          {canOrderDelivery && (
            <button
              className={`toggle-option ${
                activeOption === 'delivery' ? 'active' : ''
              }`}
              onClick={() => handleFulfillmentChange('delivery')}
              disabled={isUpdating}
              type='button'
              aria-pressed={activeOption === 'delivery'}
            >
              {t('fulfillment-general.delivery')}
            </button>
          )}
        </>
      ) : (
        <button
          className='toggle-option active'
          disabled
          type='button'
          aria-pressed={true}
        >
          {t('fulfillment-general.order')}
        </button>
      )}
    </div>
  )
}

export default RestaurantDetailsHeader
